import { vellumApiSdk } from "@vellum/micro-app-auth";
import {
  Alert,
  Badge,
  ButtonGroupSelect,
  Card,
  Checkbox,
  ColorSwatch,
  FeatureGate,
  useFeatureFlag,
  Loading,
  RecordTable,
  SelectV2,
  StatCard,
  Tabs,
  enumParam,
  numberParam,
  stringParam,
  useHashTab,
  useQuery,
  useSearchParamState,
  type BadgeTone,
  type Column,
  type SelectOption,
} from "@vellum/ui";
import {
  ProcedureType,
  type CoverageFacility,
  type CoverageVolume,
  type CoverageMap as CoverageMapData,
  type CoverageJobVolumeRow,
  type CoverageUnlocatedNurse,
  type CoverageProspects,
} from "@vellum/vellum-api";
import { useMemo, useState } from "react";

import { appUserUrl } from "@/config";
import {
  headroom,
  verdictFor,
  type Verdict,
} from "./capacity";

import { REGION_PALETTE } from "./colors";
import {
  CoverageMap,
  type CoverageLayers,
  type FacilityColorBy,
} from "./CoverageMap";
import { assignGroupColors, boundsOf, nearestNurseMiles } from "./geo";
import { BILLED_ENCOUNTERS } from "./volume";
import { visitsPerNurseWeek, workloadFor } from "./workload";

const ALL = "";
/**
 * Shows the totals row and the region capacity column while those numbers
 * are vetted.
 */
const COVERAGE_STATS_FLAG = "coverage.stats";
const ANY_TIME = 0;
/** Months back a facility's newest job must fall within to be shown. */
const ACTIVITY_OPTIONS: SelectOption<number>[] = [
  { value: 1, label: "1 month" },
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "12 months" },
  { value: ANY_TIME, label: "Any time" },
];

function monthsAgo(months: number): Date {
  const since = new Date();
  since.setMonth(since.getMonth() - months);
  return since;
}

function activeSince(months: number): Date | null {
  return months === ANY_TIME ? null : monthsAgo(months);
}

/** "Any time" still needs a finite window to average job volume over. */
const ANY_TIME_VOLUME_MONTHS = 12;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatRate(perWeek: number): string {
  return perWeek.toFixed(1);
}

const REACH_OPTIONS: SelectOption<number>[] = [15, 30, 45, 60].map(
  (miles) => ({ value: miles, label: `${miles} mi` }),
);
const SERVICE_LINES: { label: string; value: ProcedureType | typeof ALL }[] = [
  { label: "All", value: ALL },
  { label: "Infusion", value: ProcedureType.INFUSION },
  { label: "Vascular access", value: ProcedureType.VASCULAR_ACCESS },
  { label: "Transfusion", value: ProcedureType.TRANSFUSION },
];
const TAB_IDS = ["regions", "companies", "unlocated"] as const;
type TabId = (typeof TAB_IDS)[number];
const TAB_LABELS: Record<TabId, string> = {
  regions: "By region",
  companies: "By company",
  unlocated: "Missing address",
};
/** What "color by group" means; the address tab keeps region colors. */
const GROUP_LABELS: Record<TabId, string> = {
  regions: "Region",
  companies: "Company",
  unlocated: "Region",
};

type UnlocatedRow = CoverageUnlocatedNurse & { visits: number };

const VERDICT_TONES: Record<Verdict, BadgeTone> = {
  over: "strong",
  "no-nurses": "warning",
  "at-capacity": "neutral",
  room: "muted",
};

type RegionRow = {
  id: string;
  name: string;
  facilities: number;
  /** Distinct nurses who visited the region's facilities in the window. */
  nurses: number;
  jobsPerWeek: number;
  patientsPerWeek: number;
  /** The whole region's load, even when a company filter narrows the row. */
  regionJobsPerWeek: number;
  /** Nurses working in the region in an average week (nurse-weeks ÷ weeks). */
  nursesPerWeek: number;
  verdict: Verdict;
};

type CompanyRow = {
  id: string;
  name: string;
  facilities: number;
  jobsPerWeek: number;
  patientsPerWeek: number;
  outOfReach: number;
  states: string;
};

/** Weekly average of a per-facility count across the given facilities. */
function weeklyCount(
  facilities: CoverageFacility[],
  countByFacility: Map<string, number>,
  weeks: number,
): number {
  const total = facilities.reduce(
    (sum, facility) => sum + (countByFacility.get(facility.id) ?? 0),
    0,
  );
  return weeks > 0 ? total / weeks : 0;
}

function companyRows(
  facilities: CoverageFacility[],
  jobsByFacility: Map<string, number>,
  patientsByFacility: Map<string, number>,
  weeks: number,
  nearestMiles: Map<string, number>,
  reachMiles: number,
): CompanyRow[] {
  const byCompany = new Map<string, CoverageFacility[]>();
  for (const facility of facilities) {
    const list = byCompany.get(facility.company_id) ?? [];
    list.push(facility);
    byCompany.set(facility.company_id, list);
  }
  return [...byCompany.entries()]
    .map(([id, members]) => ({
      id,
      name: members[0].company_name ?? "Unknown company",
      facilities: members.length,
      jobsPerWeek: weeklyCount(members, jobsByFacility, weeks),
      patientsPerWeek: weeklyCount(members, patientsByFacility, weeks),
      outOfReach: members.filter(
        (facility) => (nearestMiles.get(facility.id) ?? Infinity) > reachMiles,
      ).length,
      states: [...new Set(members.map((facility) => facility.state))]
        .sort()
        .join(", "),
    }))
    .sort(
      (a, b) => b.facilities - a.facilities || a.name.localeCompare(b.name),
    );
}

function verdictLabel(row: RegionRow): string {
  const spare = Math.round(headroom(row.regionJobsPerWeek, row.nursesPerWeek));
  switch (row.verdict) {
    case "over":
      return `Over by ${-spare}/wk`;
    case "no-nurses":
      return "No nurses";
    case "at-capacity":
      return "At capacity";
    case "room":
      return `Room for ${spare}/wk`;
  }
}

function groupByRegion(
  facilities: CoverageFacility[],
): Map<string, CoverageFacility[]> {
  const byRegion = new Map<string, CoverageFacility[]>();
  for (const facility of facilities) {
    const list = byRegion.get(facility.region_id) ?? [];
    list.push(facility);
    byRegion.set(facility.region_id, list);
  }
  return byRegion;
}

/**
 * One row per region. Facility and job columns describe `shown` (the filtered
 * set); capacity is judged on each region's whole load (`regionFacilities`)
 * against the nurses who actually worked there, since they serve every
 * company in the region.
 */
function regionRows(
  regions: CoverageMapData["regions"],
  shown: CoverageFacility[],
  regionFacilities: CoverageFacility[],
  visitRows: CoverageJobVolumeRow[],
  jobsByFacility: Map<string, number>,
  patientsByFacility: Map<string, number>,
  weeks: number,
): RegionRow[] {
  const shownByRegion = groupByRegion(shown);
  const allByRegion = groupByRegion(regionFacilities);

  return regions
    .map((region) => {
      const members = shownByRegion.get(region.id) ?? [];
      const all = allByRegion.get(region.id) ?? [];
      const workload = workloadFor(
        visitRows,
        new Set(all.map((facility) => facility.id)),
      );
      const regionJobsPerWeek = weeks > 0 ? workload.visits / weeks : 0;
      const nursesPerWeek = weeks > 0 ? workload.nurseWeeks / weeks : 0;
      return {
        id: region.id,
        name: region.name,
        facilities: members.length,
        nurses: workload.nurses,
        jobsPerWeek: weeklyCount(members, jobsByFacility, weeks),
        patientsPerWeek: weeklyCount(members, patientsByFacility, weeks),
        regionJobsPerWeek,
        nursesPerWeek,
        verdict: verdictFor(regionJobsPerWeek, nursesPerWeek),
      };
    })
    .filter((row) => row.facilities > 0)
    .sort(
      (a, b) => b.facilities - a.facilities || a.name.localeCompare(b.name),
    );
}

// Query-string codecs for the filters, so a filtered view can be shared as a
// link. Unknown values fall back to the defaults.
const SERVICE_LINE_PARAM = enumParam(SERVICE_LINES.map((line) => line.value));
const REACH_PARAM = numberParam(REACH_OPTIONS.map((option) => option.value));
const MONTHS_PARAM = numberParam(
  ACTIVITY_OPTIONS.map((option) => option.value),
);
const COLOR_BY_PARAM = enumParam<FacilityColorBy>(["group", "reach"]);

export function CoveragePage() {
  const [serviceLine, setServiceLine] = useSearchParamState(
    "service",
    ALL,
    SERVICE_LINE_PARAM,
  );
  const [regionId, setRegionId] = useSearchParamState(
    "region",
    ALL,
    stringParam,
  );
  const [companyId, setCompanyId] = useSearchParamState(
    "company",
    ALL,
    stringParam,
  );
  const [reachMiles, setReachMiles] = useSearchParamState(
    "reach",
    30,
    REACH_PARAM,
  );
  const [activeMonths, setActiveMonths] = useSearchParamState(
    "months",
    3,
    MONTHS_PARAM,
  );
  const [colorBy, setColorBy] = useSearchParamState<FacilityColorBy>(
    "color",
    "group",
    COLOR_BY_PARAM,
  );
  const [tab, setTab] = useHashTab(TAB_IDS, { defaultTab: "regions" });
  const showStats = useFeatureFlag(COVERAGE_STATS_FLAG);
  const [layers, setLayers] = useState<CoverageLayers>({
    facilities: true,
    nurses: true,
    reach: true,
    prospects: false,
  });

  // ~15k nursing homes: only fetched once the layer is first switched on,
  // then kept so toggling it back doesn't download them again.
  const [prospectsWanted, setProspectsWanted] = useState(false);
  const prospectData = useQuery<CoverageProspects>(
    () => vellumApiSdk.adminGetCoverageProspects(),
    "Something went wrong loading CMS nursing homes.",
    [],
    { enabled: prospectsWanted },
  );
  // Homes already linked to one of our facilities show as that facility.
  const prospects = useMemo(
    () =>
      (prospectData.data?.prospects ?? []).filter(
        (prospect) => !prospect.facility_id,
      ),
    [prospectData.data],
  );

  // Job volume is averaged over the same window as the activity filter, and
  // the map includes anyone who did a visit in it.
  const volumeSince = useMemo(
    () =>
      isoDate(
        monthsAgo(
          activeMonths === ANY_TIME ? ANY_TIME_VOLUME_MONTHS : activeMonths,
        ),
      ),
    [activeMonths],
  );
  const coverage = useQuery<CoverageMapData>(
    () => vellumApiSdk.adminGetCoverageMap({ query: { since: volumeSince } }),
    "Something went wrong loading the coverage map.",
    [volumeSince],
  );
  const data = coverage.data;
  const volume = useQuery<CoverageVolume>(
    () => vellumApiSdk.adminGetCoverageVolume({ query: { since: volumeSince } }),
    "Something went wrong loading job volume.",
    [volumeSince],
  );
  const weeks = useMemo(
    () => (Date.now() - new Date(volumeSince).getTime()) / MS_PER_WEEK,
    [volumeSince],
  );
  // Visited jobs (one row per facility, week and nurse) for the service line.
  const visitRows = useMemo(
    () =>
      (volume.data?.jobs ?? []).filter(
        (row) => serviceLine === ALL || row.job_type === serviceLine,
      ),
    [volume.data, serviceLine],
  );
  // Visited jobs per facility in the window.
  const jobsByFacility = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of visitRows) {
      counts.set(row.facility_id, (counts.get(row.facility_id) ?? 0) + row.count);
    }
    return counts;
  }, [visitRows]);
  // Patients seen (billed encounters) per facility in the window.
  const patientsByFacility = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of volume.data?.encounters ?? []) {
      const line = BILLED_ENCOUNTERS[row.encounter_type];
      if (!line || (serviceLine !== ALL && line !== serviceLine)) continue;
      counts.set(row.facility_id, (counts.get(row.facility_id) ?? 0) + row.count);
    }
    return counts;
  }, [volume.data, serviceLine]);

  // Nurses who can do the selected service line, anywhere. Coverage counts
  // neighbors across region lines, so distances use these, not the region's.
  // A nurse counts for a service line if their profile lists it or they
  // actually did that kind of visit in the window (many profiles are blank).
  const visitedLines = useMemo(() => {
    const lines = new Map<string, Set<string>>();
    for (const row of volume.data?.jobs ?? []) {
      if (!row.nurse_id) continue;
      const set = lines.get(row.nurse_id) ?? new Set<string>();
      set.add(row.job_type);
      lines.set(row.nurse_id, set);
    }
    return lines;
  }, [volume.data]);
  const capableNurses = useMemo(
    () =>
      (data?.nurses ?? []).filter(
        (nurse) =>
          serviceLine === ALL ||
          nurse.capabilities.includes(serviceLine) ||
          (visitedLines.get(nurse.id)?.has(serviceLine) ?? false),
      ),
    [data, serviceLine, visitedLines],
  );

  // Facilities with a job inside the activity window; everything else on the
  // page (map, totals, both tables) is built from these.
  // With a service line picked, a facility must also have had visits of that
  // line in the window, or e.g. vascular-only sites show on the Infusion view.
  const activeFacilities = useMemo(() => {
    const since = activeSince(activeMonths);
    return (data?.facilities ?? []).filter(
      (facility) =>
        (since === null ||
          (facility.last_job_at !== null &&
            facility.last_job_at !== undefined &&
            new Date(facility.last_job_at) >= since)) &&
        (serviceLine === ALL || (jobsByFacility.get(facility.id) ?? 0) > 0),
    );
  }, [data, activeMonths, serviceLine, jobsByFacility]);

  const facilities = useMemo(
    () =>
      activeFacilities.filter(
        (facility) =>
          (regionId === ALL || facility.region_id === regionId) &&
          (companyId === ALL || facility.company_id === companyId),
      ),
    [activeFacilities, regionId, companyId],
  );
  const nurses = useMemo(
    () =>
      capableNurses.filter(
        (nurse) => regionId === ALL || nurse.region_ids.includes(regionId),
      ),
    [capableNurses, regionId],
  );

  // Over every facility (not just the filtered ones) so the company table can
  // count out-of-reach sites for companies that aren't selected.
  const nearestMiles = useMemo(
    () => nearestNurseMiles(data?.facilities ?? [], capableNurses),
    [data, capableNurses],
  );
  const outOfReach = useMemo(
    () =>
      facilities.filter(
        (facility) => (nearestMiles.get(facility.id) ?? Infinity) > reachMiles,
      ).length,
    [facilities, nearestMiles, reachMiles],
  );

  const rows = useMemo(
    () =>
      data
        ? regionRows(
            data.regions,
            facilities,
            activeFacilities,
            visitRows,
            jobsByFacility,
            patientsByFacility,
            weeks,
          )
        : [],
    [
      data,
      facilities,
      activeFacilities,
      visitRows,
      jobsByFacility,
      patientsByFacility,
      weeks,
    ],
  );
  const jobsPerWeek = weeklyCount(facilities, jobsByFacility, weeks);
  const patientsPerWeek = weeklyCount(facilities, patientsByFacility, weeks);
  // Nurses measured by who actually did visits at these facilities, not by
  // roster or home base: that's what makes jobs/nurse/week exact.
  const workload = useMemo(
    () =>
      workloadFor(
        visitRows,
        new Set(facilities.map((facility) => facility.id)),
      ),
    [visitRows, facilities],
  );
  const perNurseWeek = visitsPerNurseWeek(workload);

  // Nurses doing visits at the filtered facilities who can't be put on the
  // map, busiest first, so the addresses worth fixing come first.
  const unlocatedRows = useMemo<UnlocatedRow[]>(() => {
    const facilityIds = new Set(facilities.map((facility) => facility.id));
    const visits = new Map<string, number>();
    for (const row of visitRows) {
      if (!row.nurse_id || !facilityIds.has(row.facility_id)) continue;
      visits.set(row.nurse_id, (visits.get(row.nurse_id) ?? 0) + row.count);
    }
    return (data?.unlocated_nurses ?? [])
      .filter((nurse) => visits.has(nurse.id))
      .map((nurse) => ({ ...nurse, visits: visits.get(nurse.id) ?? 0 }))
      .sort((a, b) => b.visits - a.visits || a.name.localeCompare(b.name));
  }, [data, facilities, visitRows]);
  const nursesPerWeek = weeks > 0 ? workload.nurseWeeks / weeks : 0;
  const spare = headroom(jobsPerWeek, nursesPerWeek);

  const companies = useMemo(
    () =>
      companyRows(
        facilities,
        jobsByFacility,
        patientsByFacility,
        weeks,
        nearestMiles,
        reachMiles,
      ),
    [
      facilities,
      jobsByFacility,
      patientsByFacility,
      weeks,
      nearestMiles,
      reachMiles,
    ],
  );

  // Colors follow the open tab. Assigned over every facility, not the
  // filtered set, so a group keeps its color when you drill into it.
  const groupOf = useMemo(
    () =>
      tab === "companies"
        ? (facility: CoverageFacility) => facility.company_id
        : (facility: CoverageFacility) => facility.region_id,
    [tab],
  );
  const groupColors = useMemo(
    () =>
      assignGroupColors(data?.facilities ?? [], groupOf, REGION_PALETTE.length),
    [data, groupOf],
  );
  const facilityColors = useMemo(
    () =>
      new Map(
        (data?.facilities ?? []).map((facility) => [
          facility.id,
          groupColors.get(groupOf(facility)) ?? 0,
        ]),
      ),
    [data, groupColors, groupOf],
  );
  const groupSwatch = (groupId: string) => {
    const index = groupColors.get(groupId);
    return colorBy === "group" && index !== undefined ? (
      <ColorSwatch color={REGION_PALETTE[index]} />
    ) : null;
  };

  // Every company with a facility on file, so a pick survives changing the
  // activity window.
  const companyOptions = useMemo<SelectOption<string>[]>(() => {
    const names = new Map<string, string>();
    for (const facility of data?.facilities ?? []) {
      names.set(facility.company_id, facility.company_name ?? "Unknown company");
    }
    return [
      { value: ALL, label: "All companies" },
      ...[...names.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [data]);

  const regionOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: ALL, label: "All regions" },
      ...(data?.regions ?? []).map((region) => ({
        value: region.id,
        label: region.name,
      })),
    ],
    [data],
  );

  const focus = useMemo(
    () => boundsOf([...facilities, ...nurses]),
    [facilities, nurses],
  );

  const toggleLayer = (key: keyof CoverageLayers) => {
    if (key === "prospects") setProspectsWanted(true);
    setLayers((current) => ({ ...current, [key]: !current[key] }));
  };

  const regionColumns: Column<RegionRow>[] = [
    {
      header: "Region",
      render: (row) => (
        <button
          type="button"
          onClick={() => setRegionId(row.id === regionId ? ALL : row.id)}
          aria-pressed={row.id === regionId}
          className={`flex items-center gap-2 text-left font-medium underline-offset-2 hover:underline ${
            row.id === regionId ? "text-blue-500" : "text-gray-900"
          }`}
        >
          {groupSwatch(row.id)}
          {row.name}
        </button>
      ),
    },
    {
      header: "Facilities",
      render: (row) => row.facilities,
      className: "tabular-nums",
    },
    { header: "Nurses", render: (row) => row.nurses, className: "tabular-nums" },
    {
      header: "Jobs/wk",
      render: (row) => formatRate(row.jobsPerWeek),
      className: "tabular-nums",
    },
    {
      header: "Patients/wk",
      render: (row) => formatRate(row.patientsPerWeek),
      className: "tabular-nums",
    },
    ...(showStats
      ? [
          {
            header: "Region capacity",
            render: (row: RegionRow) => (
              <Badge tone={VERDICT_TONES[row.verdict]}>
                {verdictLabel(row)}
              </Badge>
            ),
          },
        ]
      : []),
  ];

  const unlocatedColumns: Column<UnlocatedRow>[] = [
    { header: "Nurse", render: (row) => row.name, className: "font-medium" },
    { header: "Role", render: (row) => row.role, className: "text-gray-600" },
    {
      header: "Visits",
      render: (row) => row.visits,
      className: "tabular-nums",
    },
    {
      header: "",
      render: (row) => (
        <a
          href={appUserUrl(row)}
          target="_blank"
          rel="noreferrer"
          className="font-medium whitespace-nowrap text-blue-500 no-underline hover:underline"
        >
          Fix in the app ↗
        </a>
      ),
    },
  ];

  const companyColumns: Column<CompanyRow>[] = [
    {
      header: "Company",
      render: (row) => (
        <button
          type="button"
          onClick={() => setCompanyId(row.id === companyId ? ALL : row.id)}
          aria-pressed={row.id === companyId}
          className={`flex items-center gap-2 text-left font-medium underline-offset-2 hover:underline ${
            row.id === companyId ? "text-blue-500" : "text-gray-900"
          }`}
        >
          {groupSwatch(row.id)}
          {row.name}
        </button>
      ),
    },
    {
      header: "Facilities",
      render: (row) => row.facilities,
      className: "tabular-nums",
    },
    {
      header: "Jobs/wk",
      render: (row) => formatRate(row.jobsPerWeek),
      className: "tabular-nums",
    },
    {
      header: "Patients/wk",
      render: (row) => formatRate(row.patientsPerWeek),
      className: "tabular-nums",
    },
    {
      header: `> ${reachMiles} mi`,
      render: (row) =>
        row.outOfReach ? (
          <Badge tone="warning">{row.outOfReach}</Badge>
        ) : (
          <span className="text-gray-400">0</span>
        ),
    },
    {
      header: "States",
      render: (row) => row.states,
      className: "text-gray-600",
    },
  ];

  const tabIndex = TAB_IDS.indexOf(tab);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        <ButtonGroupSelect
          inline
          label="Service line"
          options={SERVICE_LINES}
          value={serviceLine}
          onChange={setServiceLine}
        />
        <div className="w-full sm:w-72">
          <SelectV2<string>
            label="Region"
            name="coverage-region"
            options={regionOptions}
            value={regionOptions.find((option) => option.value === regionId)}
            onChange={(option) => setRegionId(option?.value ?? ALL)}
            placeholder="Search regions"
          />
        </div>
        <div className="w-full sm:w-72">
          <SelectV2<string>
            label="Company"
            name="coverage-company"
            options={companyOptions}
            value={companyOptions.find((option) => option.value === companyId)}
            onChange={(option) => setCompanyId(option?.value ?? ALL)}
            placeholder="Search companies"
          />
        </div>
        <div className="w-full sm:w-40">
          <SelectV2<number>
            label="Had a job in last"
            name="coverage-activity"
            isSearchable={false}
            options={ACTIVITY_OPTIONS}
            value={ACTIVITY_OPTIONS.find(
              (option) => option.value === activeMonths,
            )}
            onChange={(option) => option && setActiveMonths(option.value)}
          />
        </div>
        <div className="w-full sm:w-32">
          <SelectV2<number>
            label="Nurse reach"
            name="coverage-reach"
            isSearchable={false}
            options={REACH_OPTIONS}
            value={REACH_OPTIONS.find((option) => option.value === reachMiles)}
            onChange={(option) => option && setReachMiles(option.value)}
          />
        </div>
      </div>

      {coverage.error && <Alert>{coverage.error}</Alert>}
      {prospectData.error && <Alert>{prospectData.error}</Alert>}
      {volume.error && <Alert>{volume.error}</Alert>}

      <FeatureGate flag={COVERAGE_STATS_FLAG}>
        <section
          aria-label="Totals"
          className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7"
        >
          <StatCard label="Facilities on map" value={facilities.length} />
          <StatCard label="Active nurses" value={workload.nurses} />
          <StatCard
            label={`Facilities > ${reachMiles} mi from a nurse`}
            value={outOfReach}
            highlight={outOfReach > 0}
          />
          <StatCard label="Jobs / week" value={formatRate(jobsPerWeek)} />
          <StatCard
            label="Patients seen / week"
            value={formatRate(patientsPerWeek)}
          />
          <StatCard
            label="Jobs / nurse / week"
            value={perNurseWeek === null ? "—" : formatRate(perNurseWeek)}
          />
          <StatCard
            label="Spare capacity / week"
            value={workload.nurseWeeks ? Math.round(spare) : "—"}
            highlight={workload.nurseWeeks > 0 && spare < 0}
          />
        </section>
      </FeatureGate>

      <div className="flex flex-col gap-3 xl:flex-row">
        <Card className="relative h-[640px] min-w-0 flex-1 overflow-hidden">
          <CoverageMap
            facilities={facilities}
            nurses={nurses}
            prospects={prospects}
            nearestMiles={nearestMiles}
            reachMiles={reachMiles}
            layers={layers}
            colorBy={colorBy}
            facilityColors={facilityColors}
            focus={focus}
          />
          {coverage.loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <Loading />
            </div>
          )}
          {!coverage.loading && data && facilities.length === 0 && (
            <Card className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 py-3 text-sm text-gray-700">
              No facilities had a job in this window. Try a longer one.
            </Card>
          )}

          <Card className="absolute top-3 left-3 flex flex-col gap-2 bg-white p-3 text-sm">
            <ButtonGroupSelect
              label="Color facilities by"
              options={[
                { label: GROUP_LABELS[tab], value: "group" },
                { label: "Reach", value: "reach" },
              ]}
              value={colorBy}
              onChange={setColorBy}
              className="gap-0"
            />
            <Checkbox
              label="Facilities"
              checked={layers.facilities}
              onChange={() => toggleLayer("facilities")}
            />
            <Checkbox
              label="Nurses"
              checked={layers.nurses}
              onChange={() => toggleLayer("nurses")}
            />
            <Checkbox
              label={`Nurse reach (${reachMiles} mi)`}
              checked={layers.reach}
              onChange={() => toggleLayer("reach")}
            />
            <Checkbox
              label={
                prospectData.isFetching
                  ? "Nursing homes (loading…)"
                  : "Nursing homes (CMS)"
              }
              checked={layers.prospects}
              onChange={() => toggleLayer("prospects")}
            />
          </Card>

          <Card className="absolute bottom-3 left-3 flex flex-wrap items-center gap-4 bg-white px-3 py-2 text-xs text-gray-700">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-full border border-white bg-stone-900" />
              Nurse
            </span>
            {colorBy === "group" ? (
              <span className="flex items-center gap-1.5">
                <span className="flex gap-0.5">
                  {REGION_PALETTE.slice(0, 3).map((color) => (
                    <ColorSwatch key={color} color={color} />
                  ))}
                </span>
                Facility, by {GROUP_LABELS[tab].toLowerCase()}
              </span>
            ) : (
              <>
                <span className="flex items-center gap-1.5">
                  <ColorSwatch color="#ffffff" />
                  Facility in reach
                </span>
                <span className="flex items-center gap-1.5">
                  <ColorSwatch color="#b45309" />
                  Facility out of reach
                </span>
              </>
            )}
            {layers.prospects && (
              <span className="flex items-center gap-1.5">
                <svg width="14" height="12" aria-hidden>
                  <polygon
                    points="7,1 13,11 1,11"
                    fill="white"
                    stroke="#57534e"
                    strokeWidth="1.5"
                  />
                </svg>
                Nursing home, not a customer (size = beds)
              </span>
            )}
          </Card>
        </Card>

        <div className="w-full shrink-0 xl:w-[520px]">
          <Tabs
            selected={tabIndex}
            onSelect={(index) => setTab(TAB_IDS[index])}
            tabs={[
              {
                title: TAB_LABELS.regions,
                content: (
                  <div className="mt-2 max-h-[570px] overflow-y-auto">
                    <RecordTable
                      columns={regionColumns}
                      rows={rows}
                      rowKey={(row) => row.id}
                      emptyMessage={
                        coverage.loading
                          ? "Loading regions…"
                          : "No facilities match these filters."
                      }
                    />
                  </div>
                ),
              },
              {
                title: TAB_LABELS.companies,
                content: (
                  <div className="mt-2 max-h-[570px] overflow-y-auto">
                    <RecordTable
                      columns={companyColumns}
                      rows={companies}
                      rowKey={(row) => row.id}
                      emptyMessage={
                        coverage.loading
                          ? "Loading companies…"
                          : "No facilities match these filters."
                      }
                    />
                  </div>
                ),
              },
              {
                title: `${TAB_LABELS.unlocated}${
                  unlocatedRows.length ? ` (${unlocatedRows.length})` : ""
                }`,
                content: (
                  <div className="mt-2 flex max-h-[570px] flex-col gap-2 overflow-y-auto">
                    <p className="m-0 text-xs text-gray-600">
                      Nurses who did visits here but have no geocoded home
                      address, so they&apos;re missing from the map and reach.
                    </p>
                    <RecordTable
                      columns={unlocatedColumns}
                      rows={unlocatedRows}
                      rowKey={(row) => row.id}
                      emptyMessage={
                        coverage.loading
                          ? "Loading nurses…"
                          : "Every nurse doing visits here is on the map."
                      }
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>

    </div>
  );
}
